import { useEffect, useState, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner } from "@/components/Kit";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { playSfx } from "@/lib/sound";
import * as Icons from "lucide-react";
import { Store, Coins, Package, Clock, Vault as VaultIcon, RefreshCw, MoreVertical, Wrench, Tag, Gift, Trash2, Send } from "lucide-react";

function toPascal(s){return (s||"").split("-").map(w=>w.charAt(0).toUpperCase()+w.slice(1)).join("");}
const RARITY = { Junk:"#8892a6", Common:"#8892a6", Uncommon:"#2D9CFF", Rare:"#B14DFF", "Ultra-Rare":"#FF2D55", Legendary:"#FFC531" };
function fmt(s){ const m=Math.floor(s/60), r=s%60; return `${m}:${String(r).padStart(2,"0")}`; }

export default function NeonDistrict() {
  const { user, patchUser, refreshPet } = useOutletContext();
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState(null);
  const [inv, setInv] = useState(null);
  const [tab, setTab] = useState("lux");
  const [busy, setBusy] = useState(null);
  const [now, setNow] = useState(Date.now());
  const base = useRef(Date.now());
  const [giftItem, setGiftItem] = useState(null);
  const [friends, setFriends] = useState([]);

  const loadShop = () => api.get("/merchants").then(r => { base.current = Date.now(); setMerchants(r.data.merchants); });
  const loadInv = () => api.get("/inventory").then(r => setInv(r.data));
  useEffect(() => { loadShop(); loadInv(); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { // auto-refresh a merchant when its timer hits zero
    if (!merchants) return;
    const m = merchants.find(x => x.id === tab);
    if (m && m.restock_in - Math.floor((now - base.current)/1000) <= 0) loadShop();
  }, [now]); // eslint-disable-line

  const buy = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post("/shop/buy", { pet_id: it.id }); patchUser({ credits: data.credits }); playSfx("coin"); toast.success(`Bought ${it.name}`); await loadInv(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const use = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post("/inventory/use", { pet_id: it.id }); playSfx("pop"); toast.success(data.message); await loadInv(); await refreshPet(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const discard = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post("/inventory/discard", { pet_id: it.id }); toast.success(data.message); await loadInv(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const toVault = async (it) => {
    setBusy(it.id);
    try { await api.post("/vault/deposit", { pet_id: it.id }); playSfx("pop"); toast.success(`${it.name} stored in Vault`); await loadInv(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const sell = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post("/market/sell", { pet_id: it.id }); patchUser({ credits: data.credits }); playSfx("coin"); toast.success(`Sold ${it.name} for ${data.gained}c`); await loadInv(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const openGift = async (it) => {
    setGiftItem(it);
    try { const { data } = await api.get("/friends"); setFriends(data.friends); } catch { setFriends([]); }
  };
  const doGift = async (friendId) => {
    try { await api.post("/gift", { to_user: friendId, item_id: giftItem.id }); playSfx("pop"); toast.success(`Gifted ${giftItem.name}!`); setGiftItem(null); await loadInv(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!merchants || !inv) return <PageLoader label="Opening the Bazaar" />;
  const activeMerchant = merchants.find(m => m.id === tab);
  const list = tab === "inv" ? inv.items : (activeMerchant?.items || []);
  const restock = activeMerchant ? Math.max(0, activeMerchant.restock_in - Math.floor((now - base.current)/1000)) : 0;

  return (
    <div data-testid="neon-district-page">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <span className="label-cap text-primary flex items-center gap-2"><Store className="w-4 h-4" /> Neon District</span>
          <h1 className="text-4xl font-black tracking-tighter mt-2 font-head">Cyber Bazaar</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/app/vault")} data-testid="goto-vault-btn" className="btn-ghost text-xs"><VaultIcon className="w-4 h-4" /> My Vault</button>
          <div className="flex items-center gap-2 mono text-secondary font-bold" data-testid="shop-credits"><Coins className="w-5 h-5" /> {user.credits}</div>
        </div>
      </div>

      <div className="flex gap-1 mb-5 p-1 rounded-[var(--radius)] bg-muted w-fit flex-wrap">
        {merchants.map((m) => (
          <button key={m.id} data-testid={`merchant-tab-${m.id}`} onClick={() => setTab(m.id)}
            className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono ${tab===m.id?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>{m.name}</button>
        ))}
        <button data-testid="tab-inv" onClick={() => setTab("inv")}
          className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono ${tab==="inv"?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>Inventory ({inv.count})</button>
      </div>

      {tab !== "inv" && activeMerchant && (
        <div className="panel p-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="merchant-banner">
          <div>
            <div className="font-head font-bold text-sm">{activeMerchant.clerk} · {activeMerchant.name}</div>
            <p className="text-xs text-muted-foreground">{activeMerchant.tagline}</p>
          </div>
          <div className="flex items-center gap-1.5 label-cap text-secondary" data-testid="restock-timer"><Clock className="w-3.5 h-3.5" /> Restock in {fmt(restock)}</div>
        </div>
      )}
      {tab === "inv" && inv.count >= 50 && <div className="panel p-3 mb-4 text-sm border-secondary" data-testid="softcap-warning">Inventory over 50 — some actions are locked. Store items in your Vault or clear space.</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {list.length === 0 && <div className="col-span-full panel p-8 text-center text-muted-foreground">{tab==="inv"?"Inventory empty. Buy some gear!":"Sold out — check back after restock."}</div>}
        {list.map((it) => {
          const Icon = Icons[toPascal(it.icon)] || Package;
          const rc = RARITY[it.rarity] || "#8892a6";
          const afford = (user.credits || 0) >= (it.price || 0);
          return (
            <div key={it.id} data-testid={`item-${it.id}`} className="panel p-4 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <span className="w-10 h-10 rounded-[var(--radius)] grid place-items-center" style={{ background: `${rc}22`, color: rc }}><Icon className="w-5 h-5" /></span>
                <span className="label-cap text-[9px]" style={{ color: rc }}>{it.rarity}{tab==="inv"?` ·x${it.qty}`:""}</span>
              </div>
              <h3 className="font-head font-bold text-sm">{it.name}</h3>
              <p className="text-xs text-muted-foreground flex-1 mt-1 leading-snug">{it.desc}</p>
              {tab !== "inv" ? (
                <button onClick={() => buy(it)} data-testid={`buy-${it.id}`} disabled={!afford || busy===it.id} className={afford?"btn-primary w-full mt-3":"btn-ghost w-full mt-3 opacity-60"}>
                  {busy===it.id ? <Spinner className="w-4 h-4" /> : <><Coins className="w-4 h-4" /> {it.price}</>}
                </button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`item-menu-${it.id}`} disabled={busy===it.id} className="btn-secondary w-full mt-3 text-xs justify-center"><MoreVertical className="w-3.5 h-3.5" /> Actions</button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {it.category !== "scrap" && it.category !== "material" && (
                      <DropdownMenuItem data-testid={`use-${it.id}`} onClick={() => use(it)}><Wrench className="w-4 h-4 mr-2" />{it.category==="fuel"?"Use":it.category==="part"?"Install on Robot":"Equip"}</DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid={`vault-${it.id}`} onClick={() => toVault(it)}><VaultIcon className="w-4 h-4 mr-2" />Store in Vault</DropdownMenuItem>
                    {it.category !== "scrap" && <DropdownMenuItem data-testid={`sell-${it.id}`} onClick={() => sell(it)}><Coins className="w-4 h-4 mr-2" />Sell to Market</DropdownMenuItem>}
                    {it.category !== "scrap" && <DropdownMenuItem data-testid={`liststall-${it.id}`} onClick={() => navigate("/app/market")}><Tag className="w-4 h-4 mr-2" />List on Stall</DropdownMenuItem>}
                    <DropdownMenuItem data-testid={`gift-${it.id}`} onClick={() => openGift(it)}><Gift className="w-4 h-4 mr-2" />Give to Friend</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`discard-${it.id}`} onClick={() => discard(it)} className="text-accent focus:text-accent"><Trash2 className="w-4 h-4 mr-2" />Toss</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!giftItem} onOpenChange={(o) => !o && setGiftItem(null)}>
        <DialogContent data-testid="gift-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Gift className="w-5 h-5 text-accent" /> Gift {giftItem?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Send this item to a friend, free of charge.</p>
          <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
            {friends.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No friends yet. Add some in the Friends room.</p>}
            {friends.map((f) => (
              <button key={f.user_id} data-testid={`gift-to-${f.user_id}`} onClick={() => doGift(f.user_id)}
                className="w-full text-left px-3 py-2.5 rounded-[var(--radius)] hover:bg-primary hover:text-primary-foreground text-sm font-semibold flex items-center justify-between">
                {f.name} <Send className="w-4 h-4" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
